import React, { useState, useEffect } from 'react';
import { Modal, Input } from 'syngenta-digital-cropwise-react-ui-kit';
import { Select } from 'antd';
import { CreateNewFieldData } from '../types';
import { InstanceCardMap } from './InstanceCardMap';
import { parseUom } from 'services/uom';
import { useTranslation } from 'react-i18next';
import { useResourceMapperStore } from '../store/useResourceMapperStore';

export interface CreateNewFieldProps {
  show: boolean;
  close: () => void;
  activeUser: string;
}

const { Option } = Select;

const CreateNewField: React.FC<CreateNewFieldProps> = ({ show, close, activeUser }) => {
  const [fieldName, setFieldName] = useState<string>();
  const [propertyId, setPropertyId] = useState<string>();
  const [parentRegionId, setParentRegionId] = useState<string>();
  const { t } = useTranslation();

  const {
    organizationProperties: orgProperties,
    createNewFieldAndMapResource,
    selectedInstances,
  } = useResourceMapperStore();

  useEffect(() => {
    if (!selectedInstances.length) return;
    setFieldName(selectedInstances[0]?.value);
  }, [selectedInstances]);

  const getImage = (item: any) => {
    if (!item) return null;
    return (
      <div className="flex justify-center items-center h-[192px] w-[360px]">
        <InstanceCardMap item={item} fieldType={true} width={'360px'} height={'192px'} />
      </div>
    );
  };

  const handleCreateAndMap = () => {
    const newFieldData = {
      name: fieldName,
      parent_region_id: parentRegionId,
      property_id: propertyId,
      declared_area: selectedInstances[0].declared_area,
      geometry: selectedInstances[0].geometry,
    };
    createNewFieldAndMapResource(newFieldData, activeUser);
    close();
  };

  const onPropertyChange = (value: any) => {
    setPropertyId(value);
    const propertyFound = orgProperties.find((property: any) => property.id === value);
    setParentRegionId(propertyFound.root_region_id);
  };

  const generatePropertyOptions = () => {
    return orgProperties.map((property: any) => {
      return (
        <Option key={property.id} value={property.id}>
          {property.name}
        </Option>
      );
    });
  };

  if (!show) {
    return null;
  }
  return (
    <Modal
      cancelText="Cancel"
      okText="Create and Map"
      onCancel={close}
      onOk={handleCreateAndMap}
      title={t('resource-mapper.create_field')}
      visible={show}
      closable={false}
      width={766}
      wrapClassName="createNewFieldModal"
    >
      <div className="flex flex-row">
        <div className="flex flex-col">
          {getImage(selectedInstances[0])}
          <div className="flex flex-row mt-16">
            <div className="w-136px">
              <p className="font-bold">{t('resource-mapper.location')}</p>
              {selectedInstances[0]?.center ? (
                <p>
                  {selectedInstances[0].center[0].toFixed(6)},
                  <br />
                  {selectedInstances[0].center[1].toFixed(6)}
                </p>
              ) : (
                <p>--</p>
              )}
            </div>
            <div>
              <p className="font-bold">{t('resource-mapper.size')}</p>
              <p>{parseUom(selectedInstances[0]?.area_in_user_UoM) ?? '--'}</p>
            </div>
          </div>
        </div>
        <div className="flex flex-col mx-24 flex-grow" id="createNewFieldInputs">
          <p className="mb-20 text-neutral-60 font-bold">{t('resource-mapper.basic_info')}</p>
          <Input
            label="Field name"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldName(e.target.value)}
            value={fieldName}
            placeholder="field01"
            type="default"
            style={{ backgroundColor: '#FFFFFF' }}
            data-cy="create_new_field_name_input"
            id="fieldNameInput"
            className="placeholder:text-neutral-100 placeholder:opacity-100 h-40"
          />
          <p className="mt-16">{t('resource-mapper.property')}</p>
          <Select
            onChange={onPropertyChange}
            placeholder={t('file-upload.select')}
            style={{ width: '100%' }}
            data-cy="create_new_field_property_select"
          >
            {generatePropertyOptions()}
          </Select>
        </div>
      </div>
    </Modal>
  );
};

export default CreateNewField;
